import React, { useState, useEffect, useRef } from 'react';
import { FLOOR_LIST, LABELS } from '../../config';
import { useFloorScrolling } from '../../hooks/useFloorScrolling';

const CallReadyDisplay = ({
  floors,
  selectedCurrentFloor,
  selectedDestinationFloor,
  handicap,
  onFloorClick,
  onSwapFloors,
}) => {
  const noOpHandler = () => { };
  const { setFloorRef } = useFloorScrolling(noOpHandler);

  // フロアデータの準備
  const sortedFloors = [...floors].reverse(); // 必要に応じてソート
  const currentIdx = sortedFloors.findIndex(f => f.floor === selectedCurrentFloor);
  const destIdx = sortedFloors.findIndex(f => f.floor === selectedDestinationFloor);

  // 階の順序を判定 (現在の階が上にあるかどうか)
  // currentIdx < destIdx というロジックは配列の並び順依存ですが、元のコードを踏襲
  const isCurrentAbove = currentIdx < destIdx;

  const topFloorData = {
    floor: isCurrentAbove ? selectedCurrentFloor : selectedDestinationFloor,
    displayFloor: isCurrentAbove
      ? sortedFloors[currentIdx]?.frontName
      : sortedFloors[destIdx]?.frontName,
    label: isCurrentAbove ? LABELS.CURRENT : LABELS.DESTINATION,
    isCurrent: isCurrentAbove,
    isTop: true
  };

  const bottomFloorData = {
    floor: isCurrentAbove ? selectedDestinationFloor : selectedCurrentFloor,
    displayFloor: isCurrentAbove
      ? sortedFloors[destIdx]?.frontName
      : sortedFloors[currentIdx]?.frontName,
    label: isCurrentAbove ? LABELS.DESTINATION : LABELS.CURRENT,
    isCurrent: !isCurrentAbove,
    isTop: false
  };

  // 中央ボタンのアクティブ方向 ('UP' or 'DOWN')
  // isCurrentAbove (現在地が上) なら、下の行先へ行くので 'DOWN' がアクティブ
  const activeDirection = isCurrentAbove ? 'DOWN' : 'UP';

  return (
    <div
      className={FLOOR_LIST.CONTAINER_CLASS}
      style={{ height: FLOOR_LIST.CONTAINER_HEIGHT, gap: 0 }}
    >
      <div className="w-full">
        {/* 上段フロア */}
        <FloorElement
          data={topFloorData}
          setRef={setFloorRef}
          onClick={onFloorClick}
        />

        {/* 中央：アニメーション付き */}
        <ArrowSwitch
          activeDirection={activeDirection}
          handicap={handicap}
          onSwapFloors={onSwapFloors}
        />

        {/* 下段フロア */}
        <FloorElement
          data={bottomFloorData}
          setRef={setFloorRef}
          onClick={onFloorClick}
        />
      </div>
    </div>
  );
};

// --- フロア表示コンポーネント ---
const FloorElement = ({ data, setRef, onClick }) => {
  const { floor, displayFloor, label, isCurrent, isTop } = data;

  return (
    <li
      ref={setRef(floor)}
      data-floor={floor}
      onClick={() => onClick(floor, isTop)}
      className={`flex justify-center items-center mx-[calc(100vw/36)] py-[calc(100svh/144)] cursor-pointer ${isCurrent ? 'ux-bg-selected-now' : 'ux-bg-selected-go'} scale-85`}
      style={{ height: 'calc(100svh / 72 * 7)' }} // 元の計算式を維持
    >
      <div className="flex items-center justify-between w-full px-6 mx-1">
        {/* 左側スペーサー（レイアウト調整用） */}
        <div className="flex-shrink-0 opacity-0 w-8">
          <span className="text-sm ux-text-lightgray font-bold">{label}</span>
        </div>

        {/* 中央：階数画像 */}
        <div className="flex-1 flex justify-center">
          <FloorImageDisplay floor={displayFloor || floor} />
        </div>

        {/* 右側ラベル */}
        <div className="flex-shrink-0 flex justify-end w-8">
          <span className="text-sm ux-text-lightgray font-bold">{label}</span>
        </div>
      </div>
    </li>
  );
};

// --- 階数画像表示（画像がない場合はテキスト） ---
const FloorImageDisplay = ({ floor }) => {
  return (
    <div className="flex justify-center items-center space-x-1">
      {floor.split('').map((char, i) => (
        <FloorCharImage key={i} char={char} />
      ))}
    </div>
  );
};

const FloorCharImage = ({ char }) => {
  const [error, setError] = useState(false);

  if (error) {
    return <span className="text-3xl font-light text-white">{char}</span>;
  }

  return (
    <img
      src={`./assets/static/CF-W__/CF-W_${char}.png`}
      className={`${FLOOR_LIST.IMAGE_HEIGHT} w-auto`}
      alt={char}
      onError={() => setError(true)}
    />
  );
};


const ArrowSwitch = ({ activeDirection, handicap, onSwapFloors }) => {
  const containerRef = useRef(null);
  const isSwappingRef = useRef(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // アニメーション用の一時的な状態（クリック直後に反転させるため）
  const [tempActiveDirection, setTempActiveDirection] = useState(activeDirection);

  // 親からのactiveDirectionが変わったら同期する（アニメーション中でなければ）
  useEffect(() => {
    if (!isAnimating) {
      setTempActiveDirection(activeDirection);
    }
  }, [activeDirection, isAnimating]);

  const handleClick = async (e) => {
    e.stopPropagation();
    if (isSwappingRef.current) return;

    isSwappingRef.current = true;
    setIsAnimating(true);

    // 即座に見た目を反転させてアニメーション開始
    const nextDirection = tempActiveDirection === 'UP' ? 'DOWN' : 'UP';
    setTempActiveDirection(nextDirection);

    // アニメーション待機
    await new Promise(resolve => setTimeout(resolve, 600));

    // 親のデータ更新を呼ぶ
    if (onSwapFloors) {
      onSwapFloors();
    }

    // 少し待ってからフラグ解除
    setTimeout(() => {
      isSwappingRef.current = false;
      setIsAnimating(false);
    }, 50);
  };

  const containerStyle = {
    height: 'calc(100svh / 72 * 22)',
    width: 'calc(100vw / 12 * 6)',
  };

  return (
    <li className="flex justify-center items-center w-full perspective-container" style={{ height: 'calc(100svh / 72 * 22)' }}>
      <div
        ref={containerRef}
        className="m-0 p-0 relative flex items-center justify-center cursor-pointer swap-arrow-transition"
        onClick={handleClick}
        style={{
          ...containerStyle,
          perspective: '600px'
        }}
      >
        <ArrowItem
          type="UP"
          isActive={tempActiveDirection === 'UP'}
          handicap={handicap}
        />
        <ArrowItem
          type="DOWN"
          isActive={tempActiveDirection === 'DOWN'}
          handicap={handicap}
        />
      </div>
    </li>
  );
};

const ArrowItem = ({ type, isActive, handicap }) => {
  const [, setError] = useState(false);

  const imagePrefix = handicap ? 'W' : 'N';
  const stateSuffix = isActive ? `${imagePrefix}_ACTIVE` : 'INACTIVE';
  const fileName = `D_${type}_${stateSuffix}.png`;
  const src = `./assets/static/swap/${fileName}`;
  const divClasses = `m-0 p-0 flex items-center ${type === "DOWN" ? "justify-start" : "justify-end"} transition-transform ease-in-out`;

  return (
    <div
      className={divClasses}
      style={{
        width: 'calc(100vw / 12 * 3)',
        height: 'calc(100svh / 72 * 15)',
        transformStyle: 'preserve-3d',
        transform: `
          translateZ(${isActive ? 60 : -100}px)
          scale(${isActive ? 1 : 0.8})
        `,
        filter: isActive ? 'blur(0px)' : 'blur(1px)',
        transitionDuration: '600ms',
        transitionTimingFunction: 'cubic-bezier(0.4, 1, 0.2, 1)'
      }}
    >
      <img
        src={src}
        alt={type}
        onError={() => setError(true)}
        className="object-contain pointer-events-none"
        draggable={false}
        style={{
          maxWidth: '100%',
          maxHeight: '90%'
        }}
      />
    </div>
  );
};

export default CallReadyDisplay;